import { useState, useEffect, useCallback, useRef } from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Generic hook that runs an async function and tracks loading/error/data state.
 * Re-runs whenever `fn` reference changes (use stable references or useCallback).
 */
export function useAsync<T>(fn: (() => Promise<T>) | null): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const counter = useRef(0);

  const run = useCallback(() => {
    if (!fn) return;
    const callId = ++counter.current;
    setLoading(true);
    setError(null);
    fn()
      .then((result) => {
        if (callId !== counter.current) return; // stale
        setData(result);
      })
      .catch((err: unknown) => {
        if (callId !== counter.current) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (callId === counter.current) setLoading(false);
      });
  }, [fn]);

  useEffect(() => {
    const timer = setTimeout(() => {
      run();
    }, 0);
    return () => clearTimeout(timer);
  }, [run]);

  return { data, loading, error, refetch: run };
}
